"use client";

import { useCallback, useMemo } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";

//

export interface Params<Keys extends string = string> {
  toObject: () => Record<Keys, unknown>;
  paramToNum: (paramName: Keys, add?: number) => number | null | undefined;
  split: <T>(paramName: Keys, separator: string, splitter: (splited: string[]) => T) => T | null;
  get: (paramName: Keys) => string | null;
  set: (paramName: Keys, newValue: string | number | null | undefined) => void;
  delete: (paramName: Keys) => void;
  deleteAll: () => void;
  replace: (enableScroll?: boolean) => void;
  getFullPath: () => string;
  toString: () => string;
}

//

export const useParams = <Keys extends string>(): Params<Keys> => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { replace } = useRouter();
  const stringSearchParams = searchParams.toString();
  const params = useMemo(() => new URLSearchParams(stringSearchParams), [stringSearchParams]);

  const getParam = useCallback(
    (paramName: Keys) => {
      return params.get(paramName);
    },
    [params],
  );

  const toObject = useCallback(() => {
    return Object.fromEntries(params) as Record<Keys, unknown>;
  }, [params]);

  const setParam = useCallback(
    (paramName: Keys, newValue: string | number | null | undefined) => {
      if (newValue === null || newValue === undefined) return params.delete(paramName);
      return params.set(paramName, newValue.toString());
    },
    [params],
  );

  const deleteParam = useCallback(
    (paramName: Keys) => {
      return params.delete(paramName);
    },
    [params],
  );

  const deleteAllParams = useCallback(() => {
    return params.forEach((_, key) => params.delete(key));
  }, [params]);

  const getFullPath = useCallback(() => {
    return `${pathname}?${params.toString()}`;
  }, [params, pathname]);

  const toString = useCallback(() => {
    return params.toString();
  }, [params]);

  const replaceParams = useCallback(
    (enableScroll: boolean = false) => {
      const oldHref = `${pathname}?${searchParams.toString()}`;
      const newHref = getFullPath();
      if (newHref !== oldHref) replace(newHref, { scroll: enableScroll });
    },
    [getFullPath, pathname, replace, searchParams],
  );

  const paramToNum = useCallback(
    (paramName: Keys, add: number = 0) => {
      const param = getParam(paramName);
      if (param === null) return null;
      const number = Number(param);
      if (Number.isNaN(number)) return undefined;
      return number + add;
    },
    [getParam],
  );

  const split = useCallback(
    <T>(paramName: Keys, separator: string, splitter: (splited: string[]) => T) => {
      const param = getParam(paramName);
      if (param === null) return null;
      const splited = param.split(separator);
      return splitter(splited);
    },
    [getParam],
  );

  return {
    toObject,
    paramToNum,
    split,
    get: getParam,
    set: setParam,
    delete: deleteParam,
    deleteAll: deleteAllParams,
    replace: replaceParams,
    getFullPath,
    toString,
  };
};
